import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CalendarEvent,
  CalendarEventNote,
  CreateCalendarEventPayload,
  UpdateCalendarEventPayload,
} from '../../../types/calendar';
import { calendarAPI } from '../api/calendar.api';

// Matches useLeads.ts's mutation shape (mutationFn wraps the typed API call,
// onSuccess invalidates the query the write affects). useCalendarEvents keys
// its list by organizationId, so the exact key here — not the bare
// ['calendar', 'events'] prefix — refreshes only the calendar that changed
// instead of every organization's calendar on every write.

export function useCreateCalendarEvent() {
  const queryClient = useQueryClient();

  return useMutation<CalendarEvent, Error, CreateCalendarEventPayload>({
    mutationFn: async (payload) => {
      const { data } = await calendarAPI.createEvent(payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['calendar', 'events', variables.organizationId] });
    },
  });
}

export function useUpdateCalendarEvent() {
  const queryClient = useQueryClient();

  return useMutation<
    CalendarEvent,
    Error,
    { id: string; organizationId: string; payload: UpdateCalendarEventPayload }
  >({
    mutationFn: async ({ id, organizationId, payload }) => {
      const { data } = await calendarAPI.updateEvent(id, organizationId, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['calendar', 'events', variables.organizationId] });
    },
  });
}

export function useCancelCalendarEvent() {
  const queryClient = useQueryClient();

  return useMutation<CalendarEvent, Error, { id: string; organizationId: string; reason?: string }>(
    {
      mutationFn: async ({ id, organizationId, reason }) => {
        const { data } = await calendarAPI.cancelEvent(id, organizationId, reason);
        return data;
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['calendar', 'events', variables.organizationId],
        });
      },
    },
  );
}

export function useAddCalendarEventNote() {
  const queryClient = useQueryClient();

  return useMutation<
    CalendarEventNote,
    Error,
    { id: string; organizationId: string; content: string }
  >({
    mutationFn: async ({ id, organizationId, content }) => {
      const { data } = await calendarAPI.addNote(id, organizationId, content);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['calendar', 'events', variables.organizationId] });
    },
  });
}

interface RescheduleVariables {
  id: string;
  organizationId: string;
  startsAt: string;
  endsAt: string;
}

/**
 * Moving an event to a new time, optimistically.
 *
 * A drag has to move the block under the finger immediately — waiting for a
 * round trip before it moves makes the gesture feel broken. So the cache is
 * written first and the request follows.
 *
 * The server can still refuse: `assertSlotAvailable` throws
 * CalendarSlotUnavailableError when the new span collides with another active
 * event. `onError` therefore restores the exact snapshots taken in `onMutate`
 * rather than patching the event back by hand — anything else drifts from
 * what was actually on screen.
 *
 * Separate from useUpdateCalendarEvent on purpose: an edit through the form
 * has a Save button and a spinner, and should NOT move the board before the
 * server agrees.
 */
export function useRescheduleCalendarEvent() {
  const queryClient = useQueryClient();

  return useMutation<
    CalendarEvent,
    Error,
    RescheduleVariables,
    { snapshots: Array<[readonly unknown[], CalendarEvent[] | undefined]> }
  >({
    mutationFn: async ({ id, organizationId, startsAt, endsAt }) => {
      const { data } = await calendarAPI.updateEvent(id, organizationId, { startsAt, endsAt });
      return data;
    },
    onMutate: async ({ id, organizationId, startsAt, endsAt }) => {
      const queryKey = ['calendar', 'events', organizationId];
      // Otherwise an in-flight refetch can land after the optimistic write and
      // put the block back where it started, mid-drag.
      await queryClient.cancelQueries({ queryKey });

      // Every cached period holding this event, not just the visible one: the
      // same event can sit in a month cache and a week cache at once.
      const snapshots = queryClient.getQueriesData<CalendarEvent[]>({ queryKey });

      for (const [key, events] of snapshots) {
        if (!events) continue;
        queryClient.setQueryData<CalendarEvent[]>(
          key,
          events.map((event) => (event.id === id ? { ...event, startsAt, endsAt } : event)),
        );
      }

      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      for (const [key, events] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, events);
      }
    },
    // The optimistic write is a guess; the server's answer is the truth, and
    // it may also have changed syncState or the provider ids.
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['calendar', 'events', variables.organizationId],
      });
    },
  });
}
